import { useMemo } from 'react';
import { ArrowRightAlt } from '@mui/icons-material';
import { Button, Stack } from '@mui/material';
import { DataGridProps, GridColDef } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { AdminInvoicesQuery } from '../../../../api/gql';
import { InvoiceStateBadge } from '../../../../components/invoice/InvoiceStateBadge';
import { renderCellOrDefault } from '../../../../components/table/renderCellOrDefault';
import { Table } from '../../../../components/table/Table';
import { dateGridColDef } from '../../../../components/table/utils/columnHeader';
import { getInvoiceStateProperties, getRemindingToneTransKey } from '../../../../utils/invoice';

type Props = {
  invoices?: AdminInvoicesQuery['adminInvoices']['results'];
} & Partial<DataGridProps>;

export const AdminInvoicesTable = ({ invoices, ...rest }: Props) => {
  const { t } = useTranslation('page-admin-my-tasks');

  const rows = useMemo(
    () =>
      invoices?.map((invoice) => ({
        id: invoice.id,
        issueDate: invoice.createdAt,
        invoiceNumber: invoice.number,
        vendor: invoice.vendor?.name,
        customer: invoice.customer?.name,
        invoiceId: invoice.id,
        remindingTone: invoice.remindingTone,
        owner: ` ${invoice.owner?.firstName} ${invoice.owner?.lastName}`,
        state: invoice.status,
      })) ?? [],
    [invoices],
  );

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'issueDate',
        headerName: t('table.issueDate') as string,
        minWidth: 150,
        flex: 1,
        ...dateGridColDef,
      },
      {
        field: 'invoiceNumber',
        headerName: t('table.invoiceNumber') as string,
        flex: 1,
        minWidth: 130,
        renderCell: renderCellOrDefault,
      },
      {
        field: 'vendor',
        headerName: t('table.vendor') as string,
        flex: 1,
        minWidth: 140,
        renderCell: renderCellOrDefault,
      },
      {
        field: 'customer',
        headerName: t('table.customer') as string,
        flex: 1,
        minWidth: 140,
        renderCell: renderCellOrDefault,
      },
      {
        field: 'owner',
        headerName: t('table.owner') as string,
        flex: 1,
        minWidth: 140,
        renderCell: renderCellOrDefault,
      },
      {
        field: 'remindingTone',
        headerName: t('remindingTone.title', { ns: 'translation' }) as string,
        flex: 1,
        minWidth: 120,
        valueGetter: (params) => {
          return t(getRemindingToneTransKey(params.row.remindingTone), {
            ns: 'translation',
          });
        },
      },
      {
        field: 'state',
        headerName: t('table.status') as string,
        flex: 1,
        minWidth: 100,
        valueGetter: (params) => {
          // transform value to enable filtering by its translation;
          if (!params.value) {
            return params.value;
          }
          const transKey = getInvoiceStateProperties(params.value, '')?.transKey;
          return t(transKey);
        },
        renderCell: (params) => {
          return <InvoiceStateBadge state={params.row.state} />;
        },
      },
      {
        field: 'invoiceId',
        headerName: '',
        filterable: false,
        sortable: false,
        disableColumnMenu: true,
        flex: 1,
        align: 'right',
        minWidth: 200,
        renderCell: (params) => (
          <Stack direction={'row'}>
            <Button
              variant={'contained'}
              component={Link}
              to={`/admin/invoices/${params.id}`}
              endIcon={<ArrowRightAlt />}
            >
              {t('invoiceDetail')}
            </Button>
          </Stack>
        ),
      },
    ],
    [t],
  );

  return <Table id={'admin-invoices'} {...rest} columns={columns} rows={rows} />;
};
